export interface PromptOpts {
  /** Prompt text shown above the field (supports newlines). */
  message: string;
  /** Pre-filled value. */
  defaultValue?: string;
  /** Render the field as a masked password input. */
  password?: boolean;
  /** Label for the confirm button (default "OK"). */
  okLabel?: string;
  /** Max input length. */
  maxLength?: number;
}

/**
 * A DOM modal stand-in for `window.prompt` that supports masked (password) input,
 * which the native prompt can't do. The game is canvas-only, so we build a minimal
 * overlay on demand and tear it down on close. Resolves with the entered string,
 * or `null` if cancelled (Cancel button / Escape / backdrop click) — matching the
 * `window.prompt` contract so callers can keep their existing `?? ""` handling.
 *
 * Keystrokes are safe: the game's Input handler ignores keydown while an <input>
 * is focused, so typing here never triggers flap/pause.
 */
export function promptModal(opts: PromptOpts): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "1000",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.55)",
      padding: "16px",
    });

    const card = document.createElement("div");
    Object.assign(card.style, {
      width: "min(320px, 100%)",
      background: "#1a1f2b",
      color: "#f5e9c8",
      border: "1px solid #7a5e2a",
      borderRadius: "10px",
      padding: "18px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
      font: "14px system-ui, sans-serif",
    });

    const label = document.createElement("div");
    label.textContent = opts.message;
    Object.assign(label.style, {
      whiteSpace: "pre-line",
      marginBottom: "12px",
      lineHeight: "1.35",
    });

    const input = document.createElement("input");
    input.type = opts.password ? "password" : "text";
    input.value = opts.defaultValue ?? "";
    if (opts.maxLength) input.maxLength = opts.maxLength;
    input.autocomplete = opts.password ? "current-password" : "off";
    input.autocapitalize = "off";
    input.spellcheck = false;
    Object.assign(input.style, {
      boxSizing: "border-box",
      width: "100%",
      // 16px keeps iOS from zooming the page on focus.
      fontSize: "16px",
      padding: "8px 10px",
      borderRadius: "6px",
      border: "1px solid #3a4256",
      outline: "none",
      background: "#0e1118",
      color: "#e8eefc",
    });

    const buttons = document.createElement("div");
    Object.assign(buttons.style, {
      display: "flex",
      justifyContent: "flex-end",
      gap: "8px",
      marginTop: "14px",
    });

    const cancel = document.createElement("button");
    cancel.textContent = "Cancel";
    const ok = document.createElement("button");
    ok.textContent = opts.okLabel ?? "OK";
    for (const btn of [cancel, ok]) {
      Object.assign(btn.style, {
        font: "13px system-ui, sans-serif",
        padding: "7px 14px",
        borderRadius: "6px",
        border: "1px solid #7a5e2a",
        cursor: "pointer",
      });
    }
    Object.assign(cancel.style, { background: "rgba(245,233,200,0.9)", color: "#1a1f2b" });
    Object.assign(ok.style, { background: "#6b8e23", color: "#f5e9c8" });

    let done = false;
    const close = (value: string | null): void => {
      if (done) return;
      done = true;
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve(value);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        close(input.value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close(null);
      }
    };

    cancel.addEventListener("click", () => close(null));
    ok.addEventListener("click", () => close(input.value));
    overlay.addEventListener("pointerdown", (e) => {
      if (e.target === overlay) close(null);
    });
    // Capture phase so Enter/Escape resolve here before any game-level handler.
    window.addEventListener("keydown", onKey, true);

    buttons.append(cancel, ok);
    card.append(label, input, buttons);
    overlay.append(card);
    document.body.append(overlay);
    input.focus();
    input.select();
  });
}
